import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate } from 'helpers';
import { TFunc } from 'libs/i18n';
import { CountryRiskField } from 'modules/client/common/feature';
import { LegalRepresentativeMainInfoType } from 'modules/client/legalRepresentative/types';

export function useGetLegalRepresentativeInfo(
  mainInfo: LegalRepresentativeMainInfoType | undefined,
  t: TFunc,
  isCompany: boolean
) {
  const isGlobal = isGlobalProject();
  const isFinance = isFinanceProject();

  const globalInfo = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.citizenship'
                ),
                value: mainInfo.citizenship?.name,
                countryRisk: {
                  country: mainInfo.citizenship,
                  field: CountryRiskField.Citizenship,
                },
              },
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.country'
                ),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.ResidentialAddress,
                },
              },
            ],
            [
              {
                label: t('client.legalRepresentative.mainInfo.physical.phone'),
                value: mainInfo.phone,
              },
              {
                label: t('client.legalRepresentative.mainInfo.physical.email'),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.documentNumber'
                ),
                value: mainInfo.documentNumber,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.documentExpirationDate'
                ),
                value:
                  mainInfo.documentExpirationDate &&
                  formatToDate(mainInfo.documentExpirationDate),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  const financeCompanyInfo = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.company.representativeFullName'
                ),
                value: mainInfo.representativeFullName,
              },
              {
                label: t(
                  'client.legalRepresentative.mainInfo.company.representativeEmail'
                ),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.company.representativePhone'
                ),
                value: mainInfo.phone,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.company.representativeCountry'
                ),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.RegistrationAddress,
                },
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.company.documentExpirationDate'
                ),
                value:
                  mainInfo.documentExpirationDate &&
                  formatToDate(mainInfo.documentExpirationDate),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  const financePhysicalPersonInfo = useMemo(
    () =>
      mainInfo
        ? [
            [
              {
                label: t('client.legalRepresentative.mainInfo.physical.email'),
                value: mainInfo.email,
              },
              {
                label: t('client.legalRepresentative.mainInfo.physical.phone'),
                value: mainInfo.phone,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.country'
                ),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: CountryRiskField.RegistrationAddress,
                },
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.documentNumber'
                ),
                value: mainInfo.documentNumber,
              },
            ],
            [
              {
                label: t(
                  'client.legalRepresentative.mainInfo.physical.documentExpirationDate'
                ),
                value:
                  mainInfo.documentExpirationDate &&
                  formatToDate(mainInfo.documentExpirationDate),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  if (isGlobal) return globalInfo;

  if (isFinance)
    return isCompany ? financeCompanyInfo : financePhysicalPersonInfo;

  return undefined;
}
